<template>
  <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
    <h3 class="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
      <span>🔗</span>
      <span>关系模式路径分析</span>
    </h3>
    
    <div class="path-analysis-container" ref="pathContainer">
      <div class="path-columns-container" ref="columnsContainer">
        <!-- 四个维度列 -->
        <div class="path-column" ref="dimensionCol">
          <div class="path-node path-header path-header-dimension">
            四个维度
          </div>
          <div 
            v-for="dimension in dimensions" 
            :key="dimension.key"
            class="path-node path-node-dimension"
            :data-name="dimension.name"
          >
            {{ dimension.name }}
          </div>
        </div>

        <!-- 依恋类型列 -->
        <div class="path-column" ref="attachmentCol">
          <div class="path-node path-header path-header-attachment">
            依恋类型
          </div>
          <div 
            v-for="type in attachmentTypes" 
            :key="type.key"
            class="path-node"
            :class="getAttachmentNodeClass(type.key)"
            :data-name="type.key"
          >
            {{ type.name }}
          </div>
        </div>

        <!-- 恋爱情绪/感受列 -->
        <div class="path-column" ref="emotionCol">
          <div class="path-node path-header path-header-emotion">
            恋爱情绪/感受
          </div>
          <div 
            v-for="emotion in emotions" 
            :key="emotion"
            class="path-node path-node-emotion"
            :data-name="emotion"
          >
            {{ emotion }}
          </div>
        </div>

        <!-- AYP得分列 -->
        <div class="path-column" ref="aypCol">
          <div class="path-node path-header path-header-ayp">
            AYP得分
          </div>
          <div 
            class="path-node path-ayp-score path-node-ayp"
            data-name="AYP"
          >
            <div class="ayp-score-content">
              <div class="ayp-label">AYP</div>
              <div class="ayp-value">{{ aypScore }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { calculateAttachmentScores } from '@/data/personalityTypes'

export default {
  name: 'PathAnalysis',
  props: {
    scores: {
      type: Object,
      required: true
    },
    aypScore: {
      type: Number,
      required: true
    },
    attachmentType: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dimensions: [
        { key: 'MAT', name: '成熟偏好' },
        { key: 'CAR', name: '照顾需求' },
        { key: 'GUI', name: '引导欲望' },
        { key: 'IND', name: '独立倾向' }
      ],
      emotionMappings: {
        '安全-自主平衡型': ['稳定感', '新鲜感', '平衡感'],
        '探索-依赖型': ['探索欲', '依赖感', '成长需求'],
        '独立-回避型': ['独立需求', '自主感', '空间需求'],
        '矛盾-动态型': ['矛盾感', '动态需求', '灵活性']
      },
      resizeTimer: null
    }
  },
  computed: {
    attachmentScores() {
      return calculateAttachmentScores(this.scores)
    },
    attachmentTypes() {
      return [
        { key: '安全-自主平衡型', name: '安全-自主' },
        { key: '探索-依赖型', name: '探索-依赖' },
        { key: '独立-回避型', name: '独立-回避' },
        { key: '矛盾-动态型', name: '矛盾-动态' }
      ]
    },
    emotions() {
      const primary = this.attachmentType?.primary || ''
      const secondary = this.attachmentType?.secondary || ''
      const primaryEmotions = this.emotionMappings[primary] || []
      const secondaryEmotions = this.emotionMappings[secondary] || []
      return [...new Set([...primaryEmotions, ...secondaryEmotions])]
    },
    dimensionScores() {
      // 将 MAT, CAR, GUI, IND 转换为中文名称和分数
      const dimensionNames = {
        MAT: '成熟偏好',
        CAR: '照顾需求',
        GUI: '引导欲望',
        IND: '独立倾向'
      }
      return this.dimensions.map(dim => ({
        name: dimensionNames[dim.key],
        score: this.scores[dim.key] || 0
      }))
    },
    mainType() {
      return this.attachmentType?.primary || ''
    },
    secondaryType() {
      return this.attachmentType?.secondary || ''
    }
  },
  mounted() {
    // 使用多重延迟确保布局完全稳定后再绘制连接线
    this.$nextTick(() => {
      setTimeout(() => {
        requestAnimationFrame(() => {
          this.drawConnections()
        })
      }, 100)
    })
    
    // 监听窗口大小变化，重新绘制连接线
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  updated() {
    // 使用多重延迟确保布局完全稳定后再绘制连接线
    this.$nextTick(() => {
      setTimeout(() => {
        requestAnimationFrame(() => {
          this.drawConnections()
        })
      }, 50)
    })
  },
  methods: {
    handleResize() {
      // 防抖处理，避免频繁重绘
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.drawConnections()
      }, 200)
    },
    getAttachmentNodeClass(typeKey) {
      const primary = this.mainType
      const secondary = this.secondaryType
      
      if (typeKey === primary) {
        return 'path-node-attachment-primary'
      } else if (typeKey === secondary) {
        return 'path-node-attachment-secondary'
      } else {
        return 'path-node-attachment-inactive'
      }
    },
    drawConnections() {
      // 清除之前的连接线
      const existingConnections = this.$refs.pathContainer?.querySelectorAll('.path-connection')
      existingConnections?.forEach(el => el.remove())

      if (!this.$refs.columnsContainer) return

      const columns = this.$refs.columnsContainer.children
      if (columns.length < 4) return

      const dimensionCol = columns[0]
      const attachmentCol = columns[1]
      const emotionCol = columns[2]
      const aypCol = columns[3]

      // 排序维度得分
      const sortedDimensions = [...this.dimensionScores].sort((a, b) => b.score - a.score)

      // 第一列到第二列：连接最高分的两个维度到主要依恋类型
      for (let i = 0; i < 2; i++) {
        const dimensionName = sortedDimensions[i].name
        const dimensionNode = Array.from(dimensionCol.children).find(
          node => node.dataset.name === dimensionName
        )
        const attachmentNode = Array.from(attachmentCol.children).find(
          node => node.dataset.name === this.mainType
        )
        if (dimensionNode && attachmentNode) {
          this.createConnection(dimensionNode, attachmentNode, 0.8)
        }
      }

      // 连接其他两个维度到次要依恋类型
      for (let i = 2; i < 4; i++) {
        const dimensionName = sortedDimensions[i].name
        const dimensionNode = Array.from(dimensionCol.children).find(
          node => node.dataset.name === dimensionName
        )
        const attachmentNode = Array.from(attachmentCol.children).find(
          node => node.dataset.name === this.secondaryType
        )
        if (dimensionNode && attachmentNode) {
          this.createConnection(dimensionNode, attachmentNode, 0.6)
        }
      }

      // 第二列到第三列：主要依恋类型连接到对应的情绪/感受
      const mainAttachmentNode = Array.from(attachmentCol.children).find(
        node => node.dataset.name === this.mainType
      )
      if (mainAttachmentNode && this.emotionMappings[this.mainType]) {
        this.emotionMappings[this.mainType].forEach(emotion => {
          const emotionNode = Array.from(emotionCol.children).find(
            node => node.dataset.name === emotion
          )
          if (emotionNode) {
            this.createConnection(mainAttachmentNode, emotionNode, 0.7)
          }
        })
      }

      // 次要依恋类型连接到对应的情绪/感受
      const secondaryAttachmentNode = Array.from(attachmentCol.children).find(
        node => node.dataset.name === this.secondaryType
      )
      if (secondaryAttachmentNode && this.emotionMappings[this.secondaryType]) {
        this.emotionMappings[this.secondaryType].forEach(emotion => {
          const emotionNode = Array.from(emotionCol.children).find(
            node => node.dataset.name === emotion
          )
          if (emotionNode) {
            this.createConnection(secondaryAttachmentNode, emotionNode, 0.5)
          }
        })
      }

      // 第三列到第四列：所有情绪/感受节点连接到AYP得分
      const aypNode = Array.from(aypCol.children).find(
        node => node.dataset.name === 'AYP'
      )
      if (aypNode) {
        Array.from(emotionCol.children).forEach(emotionNode => {
          if (emotionNode.dataset.name && emotionNode.dataset.name !== '恋爱情绪/感受') {
            this.createConnection(emotionNode, aypNode, 0.6)
          }
        })
      }
    },
    createConnection(from, to, opacity) {
      if (!this.$refs.pathContainer) return

      const fromRect = from.getBoundingClientRect()
      const toRect = to.getBoundingClientRect()
      const containerRect = this.$refs.pathContainer.getBoundingClientRect()

      const fromX = fromRect.left - containerRect.left + fromRect.width / 2
      const fromY = fromRect.top - containerRect.top + fromRect.height / 2
      const toX = toRect.left - containerRect.left + toRect.width / 2
      const toY = toRect.top - containerRect.top + toRect.height / 2

      const dx = toX - fromX
      const dy = toY - fromY
      const length = Math.sqrt(dx * dx + dy * dy)
      const angle = Math.atan2(dy, dx) * 180 / Math.PI

      const connection = document.createElement('div')
      connection.className = 'path-connection'
      connection.style.width = `${length}px`
      connection.style.height = '3px'
      connection.style.left = `${fromX}px`
      connection.style.top = `${fromY}px`
      connection.style.transform = `rotate(${angle}deg)`
      connection.style.opacity = opacity
      connection.style.background = 'linear-gradient(135deg, var(--psychology-primary) 0%, var(--psychology-secondary) 100%)'
      connection.style.position = 'absolute'
      connection.style.zIndex = '1'
      connection.style.transformOrigin = '0 0'
      connection.style.borderRadius = '2px'
      connection.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)'

      this.$refs.pathContainer.appendChild(connection)
    }
  }
}
</script>

<style scoped>
.path-analysis-container {
  overflow-x: auto;
  overflow-y: visible;
  padding: 24px;
  position: relative;
  background: linear-gradient(135deg, #FFF5F5 0%, #F0F5FF 100%);
  border-radius: 16px;
  min-height: 300px;
  display: flex;
  justify-content: center;
  /* 优化滚动体验 */
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.path-columns-container {
  display: flex;
  gap: 24px;
  min-width: max-content;
  position: relative;
  overflow: visible;
  align-items: flex-start;
  margin: 0 auto;
  flex-shrink: 0;
}

.path-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 120px;
  position: relative;
  align-items: center;
}

.path-node {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.75rem;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
  word-break: break-word;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.path-node:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

/* 四个维度节点样式 */
.path-header-dimension {
  width: 120px;
  height: 48px;
  border-radius: 14px;
  font-size: 0.85rem;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #FF9800 0%, #FFB74D 100%);
}

.path-node-dimension {
  background: linear-gradient(135deg, #FF9800 0%, #FFB74D 100%);
}

/* 依恋类型节点样式 */
.path-header-attachment {
  width: 120px;
  height: 48px;
  border-radius: 14px;
  font-size: 0.85rem;
  margin-bottom: 6px;
  background: linear-gradient(135deg, var(--psychology-primary) 0%, var(--psychology-secondary) 100%);
}

.path-node-attachment-primary {
  background: linear-gradient(135deg, var(--psychology-primary) 0%, var(--psychology-secondary) 100%);
  opacity: 1;
  border-color: rgba(255, 255, 255, 0.5);
}

.path-node-attachment-secondary {
  background: linear-gradient(135deg, var(--psychology-primary) 0%, var(--psychology-secondary) 100%);
  opacity: 0.75;
  border-color: rgba(255, 255, 255, 0.3);
}

.path-node-attachment-inactive {
  background: linear-gradient(135deg, #9E9E9E 0%, #BDBDBD 100%);
  opacity: 0.5;
  border-color: rgba(255, 255, 255, 0.2);
}

/* 情绪/感受节点样式 */
.path-header-emotion {
  width: 120px;
  height: 48px;
  border-radius: 14px;
  font-size: 0.85rem;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #4CAF50 0%, #81C784 100%);
}

.path-node-emotion {
  background: linear-gradient(135deg, #4CAF50 0%, #81C784 100%);
}

/* AYP得分节点样式 */
.path-header-ayp {
  width: 120px;
  height: 48px;
  border-radius: 14px;
  font-size: 0.85rem;
  margin-bottom: 6px;
  background: linear-gradient(135deg, var(--psychology-primary) 0%, var(--psychology-secondary) 100%);
}

.path-ayp-score {
  width: 110px;
  height: 110px;
  font-size: 0.8rem;
}

.path-node-ayp {
  background: linear-gradient(135deg, var(--psychology-primary) 0%, var(--psychology-secondary) 100%);
}

.ayp-score-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.ayp-label {
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.95;
}

.ayp-value {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1;
}

.path-connection {
  position: absolute;
  height: 3px;
  background: linear-gradient(135deg, var(--psychology-primary) 0%, var(--psychology-secondary) 100%);
  z-index: 1;
  transform-origin: 0 0;
  opacity: 0.6;
  border-radius: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: opacity 0.3s ease;
}

.path-connection:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .path-analysis-container {
    padding: 16px;
    justify-content: flex-start;
    /* 确保可以左右滚动 */
    overflow-x: auto;
    overflow-y: visible;
  }

  /* 保持横向布局，不改变布局方向 */
  .path-columns-container {
    gap: 20px;
    margin: 0;
    /* 确保有足够的最小宽度以触发滚动 */
    min-width: max-content;
  }
  
  .path-column {
    /* 保持纵向排列，不改为横向 */
    flex-direction: column;
    min-width: 100px;
    gap: 10px;
  }
  
  .path-header {
    width: 100px;
    height: 44px;
    font-size: 0.8rem;
    margin-bottom: 0;
  }
  
  .path-node {
    width: 80px;
    height: 80px;
    font-size: 0.7rem;
  }
  
  .path-ayp-score {
    width: 90px;
    height: 90px;
  }

  .ayp-value {
    font-size: 1.1rem;
  }
  
  .path-connection {
    display: block !important;
  }
}

@media (max-width: 480px) {
  .path-analysis-container {
    padding: 12px;
  }

  .path-columns-container {
    gap: 16px;
  }

  .path-column {
    min-width: 90px;
    gap: 8px;
  }

  .path-node {
    width: 70px;
    height: 70px;
    font-size: 0.65rem;
  }

  .path-header {
    width: 90px;
    height: 40px;
    font-size: 0.75rem;
  }

  .path-ayp-score {
    width: 80px;
    height: 80px;
  }

  .ayp-value {
    font-size: 1rem;
  }

  .ayp-label {
    font-size: 0.7rem;
  }
}
</style>
